import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/app-shell';
import { Alert, Button, Card, Field, Input } from '@/components/ui';
import { fieldMessages, messageForError } from '@/lib/errors';
import { useAuth } from '@/providers/auth';
import { useToast } from '@/providers/toast';

const MIN_LENGTH = 12;

export function ChangePasswordPage({ forced }: { forced?: boolean }) {
  const { t } = useTranslation();
  const { changePassword, signOut } = useAuth();
  const { notify } = useToast();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_LENGTH;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (mismatch || tooShort) return;

    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
      // The session is over by the time this resolves — the server revoked it —
      // so there is nowhere to navigate to. Clearing the user drops the app to
      // the sign-in screen by itself; the toast is what explains why.
      notify(t('auth.changedSignInAgain'), 'success');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  const form = (
    <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
      {forced && <Alert tone="info">{t('auth.changePasswordForced')}</Alert>}

      {error != null && <Alert tone="danger">{messageForError(t, error)}</Alert>}

      <Field
        label={t('auth.currentPassword')}
        htmlFor="current"
        required
        errors={fieldMessages(t, error, 'currentPassword')}
      >
        <Input
          id="current"
          type="password"
          dir="ltr"
          autoComplete="current-password"
          required
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
      </Field>

      <Field
        label={t('auth.newPassword')}
        htmlFor="next"
        required
        hint={t('auth.passwordRule')}
        errors={[
          ...fieldMessages(t, error, 'newPassword'),
          ...(tooShort ? [t('errors.PASSWORD_TOO_SHORT')] : []),
        ]}
      >
        <Input
          id="next"
          type="password"
          dir="ltr"
          autoComplete="new-password"
          minLength={MIN_LENGTH}
          required
          value={next}
          onChange={(event) => setNext(event.target.value)}
        />
      </Field>

      <Field
        label={t('auth.confirmPassword')}
        htmlFor="confirm"
        required
        errors={mismatch ? [t('auth.passwordMismatch')] : []}
      >
        <Input
          id="confirm"
          type="password"
          dir="ltr"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />
      </Field>

      <div className="flex gap-2">
        <Button type="submit" variant="primary" loading={busy} disabled={mismatch || tooShort}>
          {t('auth.changeSubmit')}
        </Button>
        {forced && (
          // A person who cannot change it right now must still be able to leave
          // rather than being stuck on a screen with no exit.
          <Button variant="ghost" onClick={() => void signOut()}>
            {t('app.signOut')}
          </Button>
        )}
      </div>
    </form>
  );

  if (forced) {
    return (
      <div className="grid min-h-full place-items-center p-4">
        <div className="w-full" style={{ maxInlineSize: '28rem' }}>
          <h1 className="dio-display mb-4 text-2xl">{t('auth.changePasswordTitle')}</h1>
          <Card>{form}</Card>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader title={t('auth.changePasswordTitle')} />
      <div style={{ maxInlineSize: '32rem' }}>
        <Card>{form}</Card>
      </div>
    </>
  );
}
